import React, { useMemo, useCallback, useState } from 'react';
import Select, { createFilter } from 'react-select';
import { selectStyles } from '../../config/constants';
import { ValueContainer, DropdownIndicator, InputOption } from '../../config/customSelect';
import CustomMenuList from '../Helper/CustomMenuList';

const SelectAllSearch = ({
  options = [],
  onChange,
  isMulti = true,
  selectAllLabel = 'Select All',
  selectAllMatchingLabel = 'Select All Matching',
  enableSelectAll = true,
  value,
  inputValue: externalInputValue = '',
  onInputChange,
  filterOption,
  ...props
}) => {
  const [internalInputValue, setInternalInputValue] = useState(externalInputValue);
  const [filteredSearchResults, setFilteredSearchResults] = useState([]);

  const selectAllOption = useMemo(() => ({
    value: '__select_all__',
    label: selectAllLabel,
    isSelectAll: true,
  }), [selectAllLabel]);

  const selectAllMatchingOption = useMemo(() => ({
    value: '__select_all_matching__',
    label: selectAllMatchingLabel,
    isSelectAllMatching: true,
  }), [selectAllMatchingLabel]);

  const optionsWithSelectAll = useMemo(() => {
    if (!isMulti || !enableSelectAll) {
      return options;
    }
    const allOptions = [...options];
    if (internalInputValue) {
      allOptions.unshift(selectAllMatchingOption);
    } else {
      allOptions.unshift(selectAllOption);
    }
    return allOptions;
  }, [options, internalInputValue, isMulti, enableSelectAll, selectAllOption, selectAllMatchingOption]);

  const handleChange = useCallback((newValue) => {
    if (!newValue) {
      onChange?.(newValue);
      return;
    }
    if (Array.isArray(newValue)) {
      const lastValue = newValue[newValue.length - 1];
      if (lastValue?.isSelectAll) {
        const allSelectableOptions = options.filter(
          opt => !opt.isSelectAll && !opt.isSelectAllMatching
        );
        onChange?.(allSelectableOptions);
        return;
      }
      if (lastValue?.isSelectAllMatching) {
        onChange?.(filteredSearchResults);
        return;
      }
      const filtered = newValue.filter(v => !v.isSelectAll && !v.isSelectAllMatching);
      onChange?.(filtered);
    } else {
      onChange?.(newValue);
    }
  }, [onChange, options, filteredSearchResults]);

  const handleInputChange = useCallback((inputVal, actionMeta) => {
    setInternalInputValue(inputVal);
    if (inputVal) {
      const filteredOpts = options.filter(opt => {
        const label = opt.label || '';
        return label.toLowerCase().includes(inputVal.toLowerCase());
      });
      setFilteredSearchResults(filteredOpts);
    } else {
      setFilteredSearchResults([]);
    }
    
    onInputChange?.(inputVal, actionMeta);
  }, [onInputChange, options]);



  return (
    <Select
      {...props}
      captureMenuScroll={false}
      hideSelectedOptions={false}
      classNamePrefix="custom-select "
      options={optionsWithSelectAll}
      onChange={handleChange}
      onInputChange={handleInputChange}
      value={value}
      isMulti={isMulti}
      styles={selectStyles}
      components={{
        Option: InputOption,
        ValueContainer, 
        DropdownIndicator, 
        MenuList: CustomMenuList,
        ...props.components,
      }}
      filterOption={(option, inputValue) => {
        if (option.data?.isSelectAll || option.data?.isSelectAllMatching) {
          return true;
        }
        return createFilter({ ignoreAccents: false })(option, inputValue);
      }}
    />
  );
};

export default SelectAllSearch;
